import { isUnaccentedText } from "../resolvers/name-text.js";
import { normalizeViText } from "../vi-normalizer.js";

/**
 * 🔴 KHÔNG LẶP DANH TỪ khi chèn TÊN vào câu — MỘT luật cho mọi câu mẫu có "<danh từ> {tên}".
 *
 * US-AIV2-0113 M01 (@UC0192 @UC0179) làm cho "nhóm"; US-AIV2-0114 L03 (@UC0205 @UC9064) tổng quát hoá: câu "Quỹ {tenQuy}: …" + quỹ tên "Quỹ chung"
 * ⇒ đo 28/09 *"Quỹ Quỹ chung: tháng 9/2026 thu…"*; thông báo "Nhóm {groupName} vừa mở quỹ…" + nhóm tên "nhóm đội bóng…" ⇒ *"Nhóm nhóm đội bóng…"*.
 *
 * Luật: CỤM thuộc một LỚP DANH TỪ (`NAME_NOUN_CLASSES`) ĐỨNG RIÊNG ngay trước chỗ trống (cụm dài nhất: "Khoản chi {x}" là "khoản chi", không "chi"),
 * và giá trị BẮT ĐẦU bằng một cụm CÙNG lớp ⇒ bỏ cụm của CÂU.
 *  - So sau NFC, không phân biệt hoa/thường, ĐÚNG BIÊN TỪ ("Quỹnày", "Nhóm1", "Nhàn", "Chiến" không phải), CÓ DẤU ("Quyết tâm", "Quy Nhơn FC", "Nhôm
 *    Kính", "Chị Lan" không phải "quỹ"/"nhóm"/"chi"). Giá trị gõ KHÔNG một dấu nào (`isUnaccentedText` — cùng vị-từ với bộ đọc tên) VÀ có từ HAI chữ
 *    trở lên ("nhom ban than", "quy lop 3a") ⇒ so bỏ dấu. Một chữ không dấu ("Quy", "QUY" — thường là TÊN NGƯỜI) ⇒ không bỏ dấu, giữ tiền tố
 *    ("Quỹ Quy trả …", không "Quy trả …" đọc như người tên Quy trả tiền — review #1116 M2).
 *  - Lớp gồm chữ tương đương của mọi locale trong bundle (vi/en): thông báo tiếng Anh "Group {groupName}" + "nhóm đội bóng" cũng là lặp.
 *  - Tên CHỈ là danh từ trần ("Nhóm", "Quỹ") ⇒ cũng chỉ một chữ ("Quỹ: …", "Nhóm mới có mình bạn") — không in "Quỹ Quỹ".
 *  - Giá trị người dùng giữ nguyên từng ký tự — chỉ viết hoa chữ đầu khi cụm bị bỏ viết hoa (đầu câu). Tên trong ngoặc (“{tenDot}”) không có chữ
 *    đứng RIÊNG ngay trước chỗ trống ⇒ giữ nguyên.
 *
 * Tập lớp do LƯỚI ĐẢO CHIỀU quyết (`name-noun.test.ts`, review #1116 M1): với MỌI chỗ trống TÊN trong Sổ · thẻ Người dẫn đường · thông báo, cụm đứng
 * trước phải được `nameNounBeforeRe()` nhận, hoặc chữ đó có lý do tha (giới từ "cho/của/for…", "biểu quyết", "chi khẩn cấp"). Bản trước liệt kê TAY
 * danh từ ứng viên và sót "Khoản chi {expenseName}" (⇒ *"Khoản chi Chi mua hoa"*) và "Nhà {counterpartyName}" (fixture thật "Nhà bác Tư" ⇒ *"Nhà Nhà
 * bác Tư có việc"*). Lớp "khoản chi" gồm cả "chi": tên khoản chi hay bắt đầu bằng động từ "Chi …" ("Chi mua hoa") ⇒ "Chi mua hoa đã được duyệt".
 *
 * Người dùng: `renderTemplate` (Sổ) · `renderCardText` (Người dẫn đường) · `renderNotification` (thông báo, worker render theo locale người nhận)
 * — mỗi bộ chèn giữ NGUYÊN regex chỗ trống của nó; câu dựng tay ở binding dùng `joinNameNoun` (lưới `name-noun-literal.invariants.spec.ts` dựng
 * tập cụm từ CHÍNH hằng này).
 */
export const NAME_NOUN_CLASSES: readonly (readonly string[])[] = [
  ["nhóm", "group"],
  ["quỹ", "fund"],
  ["khoản chi", "chi", "expense"],
  ["nhà"],
];

const lower = (s: string): string => s.normalize("NFC").toLocaleLowerCase("vi");

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");

/** Cụm danh từ của CÂU (thuộc `classes`), đứng riêng, ngay trước chỗ trống (một dấu cách); cụm dài trước. So sau NFC, không phân biệt hoa/thường. */
export function nameNounBeforeRe(classes: readonly (readonly string[])[] = NAME_NOUN_CLASSES): RegExp {
  const alternation = [...classes.flat()].sort((a, b) => b.length - a.length).map(escapeRe).join("|");
  return new RegExp(`(?<![\\p{L}\\p{M}\\p{N}])(${alternation}) $`, "iu");
}

const NOUN_BEFORE_RE = nameNounBeforeRe();

function classOf(noun: string): readonly string[] | undefined {
  const w = lower(noun);
  return NAME_NOUN_CLASSES.find((c) => c.includes(w));
}

/** `text` bắt đầu bằng cụm `phrase` và dừng đúng biên từ. */
function startsWithPhrase(text: string, phrase: string): boolean {
  return text.startsWith(phrase) && !/^[\p{L}\p{M}\p{N}]/u.test(text.slice(phrase.length));
}

const wordCount = (value: string): number => value.match(/[\p{L}\p{M}\p{N}]+/gu)?.length ?? 0;

/** Giá trị bắt đầu bằng một cụm cùng lớp với danh từ của câu — đúng biên từ, có dấu (bỏ dấu chỉ khi CẢ giá trị gõ không dấu và có ≥2 chữ). */
function startsWithSameNoun(noun: string, value: string): boolean {
  const nounClass = classOf(noun);
  const v = lower(value).trimStart();
  if (nounClass === undefined || v === "") return false;
  if (nounClass.some((p) => startsWithPhrase(v, p))) return true;
  if (!isUnaccentedText(value) || wordCount(value) < 2) return false;
  return nounClass.some((p) => startsWithPhrase(normalizeViText(v), normalizeViText(p)));
}

function upperFirst(value: string): string {
  const [first = "", ...rest] = Array.from(value.trimStart());
  return first.toLocaleUpperCase("vi") + rest.join("");
}

function isCapitalNoun(noun: string): boolean {
  const first = noun.charAt(0);
  return first !== first.toLocaleLowerCase("vi");
}

/** "<danh từ> " + tên — cùng luật với chỗ trống (câu dựng tay ở binding: "Ở nhóm X", "Quỹ X trả …"). `noun` là một cụm của `NAME_NOUN_CLASSES`. */
export function joinNameNoun(noun: string, value: string): string {
  if (!startsWithSameNoun(noun, value)) return `${noun} ${value}`;
  return isCapitalNoun(noun) ? upperFirst(value) : value.trimStart();
}

/**
 * Thay mọi chỗ trống khớp `placeholderRe` (cờ `g`, nhóm bắt 1 = tên chỗ trống) bằng `valueOf(tên, khớp)`, áp luật không-lặp-danh-từ. Một lượt trên
 * template gốc ⇒ `{x}` nằm TRONG giá trị không bị thay tiếp. `khớp` cho bộ chèn cần thêm phần của chỗ trống (kiểu `{amount, money, VND}`).
 */
export function replacePlaceholdersWithNameNoun(
  template: string,
  placeholderRe: RegExp,
  valueOf: (name: string, match: RegExpMatchArray) => string,
): string {
  let out = "";
  let last = 0;
  for (const m of template.matchAll(placeholderRe)) {
    const at = m.index ?? 0;
    const head = template.slice(last, at);
    const value = valueOf(m[1] ?? "", m);
    const nfcHead = head.normalize("NFC");
    const noun = NOUN_BEFORE_RE.exec(nfcHead);
    out += noun === null ? head + value : nfcHead.slice(0, nfcHead.length - noun[0].length) + joinNameNoun(noun[1]!, value);
    last = at + m[0].length;
  }
  return out + template.slice(last);
}
